import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { api } from "../api";
import { playAcknowledgement, playPeonLine } from "../audio";
import { formatAgo } from "../format";
import { canHeart, hasHearted, heartsCountLabel, holdHeart, isNoteActive, noteExpiresAt, noteFireAt, noteHasPhoto, noteHeartsLabel, noteSlipLabel, NOTE_PHOTO, noteSlipText, noteTilt, NOTE_ADD_PHOTO, NOTE_CAPTION_PLACEHOLDER, NOTE_MAX_CHARS, NOTE_REMOVE_PHOTO, NOTE_SLIP_HINT, timerMs, timerPartsLabel, toggleHeart, type TimerParts } from "../../shared/notes";
import { useHeartsUI, usePhotoNotesUI, useTimersUI } from "../features";
import { cardRinging, isPicked, NO_TIMER, noteMetaLine, noteToRing, RING_EVENT, rosterWithMe, startRing, stopAndAck, stopIfAcknowledged, TIMER_PART_MAX, timerRequest, togglePicked, withTimerPart, type Picked } from "../ring";
import type { Note, Role } from "../../shared/types";
import { canDelete } from "../../shared/permissions.ts";
import { ErrorBox } from "./Shell";
import { PixelIcon } from "./PixelArt";
import { PeonStamp } from "./PeonStamp";
import { DogEarSheet, SLIP_HINT_ID } from "./DogEar";
import { NoteHeart } from "./NoteHeart";
import { MentionField } from "./MentionField";
import { MentionText } from "./MentionText";
import { useLongPress } from "./DoneExtras";
import { BusyLabel } from "./BusyLabel";
import { ComposerSheet, holdKeyboard } from "./ComposerSheet";
import { noteMissing, type Missing } from "../../shared/form-missing.ts";
import type { SpeedrunNote } from "../../shared/speedrun";
import { SpeedrunSlip, SPEEDRUN_SLIP_ID } from "./SpeedrunNote";
import { NoticeSlips } from "./NoticeSlips";
import { SlopSign } from "./SlopSign";
import { WeekSign } from "./WeekSign";
import type { HouseWeekSummary } from "../../shared/house-week.ts";
import type { SlopTally } from "../../shared/slop";
import { PhotoPicker } from "./PhotoPicker";

/** The add slip's fixed id for its tilt (shared/notes.ts noteTilt), the same on both clients. */
const ADD_SLIP_ID = "add";

/** The id a note pinned on this device carries until the server answers with its own. */
const LOCAL_PREFIX = "local:";
const newestFirst = (a: Note, b: Note) => b.created_at.localeCompare(a.created_at);

/** The three wheels the alarm bell opens, in the phone's own order and words. */
const TIMER_WHEELS: readonly { unit: keyof TimerParts; word: string }[] = [
  { unit: "hours", word: "hours" },
  { unit: "minutes", word: "min" },
  { unit: "seconds", word: "sec" },
];

/**
 * One note on the notice board (owner, 2026-10-05: "Pinned notes should also actually look like pinned
 * notes on a notice board, clicking on them will expand them. Think animal crossing note board."):
 * a paper slip pinned to the cork, a little crooked (shared/notes.ts noteTilt), the author's peon
 * stamp in place of their name (owner, 2026-10-05: "Rather than the peons name on the minimized
 * notes, it should just be a little stamp of their peon"; PeonStamp, named by the author's name for a
 * screen reader, shared/notes.ts NOTE_STAMP) and the first line or so (noteSlipText). A tap opens it in place to the whole note, straight and full
 * width, with everything the card used to show and its buttons; a tap on its paper folds it again.
 * The paper is one button in both states, so the focus stays on it. A note ringing for this person
 * wears the urgent card's red frame and "!" and is never tilted (src/ring.ts).
 */
function NoteSlip({ note, now, me, open, onToggle, onRemove, onAck, onHeart, mayRemove, heartsUI }: { note: Note; now: number; me: string; open: boolean; onToggle: () => void; onRemove: (note: Note) => void; onAck: (note: Note) => void; onHeart: (note: Note, on: boolean) => void; mayRemove: boolean; heartsUI: boolean }) {
  /** A note just pinned on this device, still on its way to the server: shown, not yet removable. */
  const sending = note.id.startsWith(LOCAL_PREFIX);
  // Hearts (docs/notes.md "Hearts"), only while heartsUI is on: the count in the closed slip's
  // bottom-right corner, and on the open slip the heart button (anyone but the author) and who.
  const hearts = heartsUI ? (note.hearts ?? []) : [];
  const mayHeart = heartsUI && !sending && canHeart(note, me);
  const mine = hasHearted(hearts, me);
  const showHearts = heartsUI && (mayHeart || hearts.length > 0);
  /** Goes up on each heart you add, so the heart's pop plays again (CSS, hard frames, none with reduced motion). */
  const [pops, setPops] = useState(0);
  function heart() {
    if (!mine) setPops((n) => n + 1);
    onHeart(note, !mine);
  }
  // A long press on the closed slip hearts it without opening it (owner, 2026-10-12: "make long
  // pressing on the notes add a heart as well"): it only adds (shared/notes.ts holdHeart), the
  // corner's heart pops, and the click it ends in is swallowed (useLongPress, the crown's: 500 ms, a
  // finger that moves 10px is a scroll). Not on the author's own note, nor with the flag off: there it
  // is an ordinary press and opens the slip on release.
  /** Goes up on each long press, so the closed slip's corner heart pops again; `popping` until it ends, so a slip closed later does not replay it. */
  const [cornerPops, setCornerPops] = useState(0);
  const [popping, setPopping] = useState(false);
  const holdable = !open && mayHeart;
  const long = useLongPress(holdable ? holdByLong : null);
  function holdByLong() {
    const held = holdHeart(note, hearts, me);
    if (!held) return;
    setCornerPops((n) => n + 1);
    setPopping(true);
    if (held.send) onHeart(note, true);
  }
  // The closed slip's heart tag is a button of its own for anyone who may heart the note (owner,
  // 2026-10-12, on a visible heart over a double tap: "Yeah go ahead with visible"): always there, an
  // outline at zero, the count once anyone has hearted, filled red once yours. A tap toggles yours as
  // the open slip's heart does, and never opens the slip; the corner's heart pops when you add it.
  function tapCorner() {
    if (!mine) {
      setCornerPops((n) => n + 1);
      setPopping(true);
    }
    onHeart(note, !mine);
  }

  function remove() {
    if (!confirm("Remove this note?")) return;
    onRemove(note);
  }

  // A timed note fired for this person rings here until anyone gets it (src/ring.ts): the urgent
  // card's red frame, and Stop as its one button. Stop ends it here at once and for everyone.
  const ringing = cardRinging(note, me, now);
  function stop() {
    void stopAndAck(note.id, api.notes.ack).then((acked) => {
      if (acked) onAck(acked);
    });
  }

  const straight = open || ringing;
  const style = straight ? undefined : { transform: `rotate(${noteTilt(note.id)}deg)` };
  // The dog-ear says a tap opens it (shared/notes.ts DOG_EAR): every closed slip but a ringing one, whose red frame says more.
  const earred = !open && !ringing;
  // A photo note (docs/notes.md "Photo notes"): a little Polaroid on the cork, drawn whatever the
  // flag says, so a photo pinned while it was on never vanishes. A tap on the photo opens and folds the
  // note as a tap on its paper does, never a photo viewer (owner, 2026-10-23: "The photo viewer
  // shouldn't activate when clicking photos pinned to the board, they can just be expanded like normal
  // notes"). The photo is a real <img> of the full photo, so a long press on it is the phone's own
  // "Save image" (and a right click the computer's): the long press to heart leaves it alone, the
  // heart scrap still hearts.
  const photoId = noteHasPhoto(note) ? note.photo_id! : null;
  const [photoRatio, setPhotoRatio] = useState<number | null>(null);
  const onPhoto = (e: Event) => photoId !== null && e.target instanceof Element && e.target.closest(".slip-photo") !== null;
  const hold = photoId === null ? long : { ...long, onPointerDown: (e: PointerEvent) => !onPhoto(e) && long.onPointerDown(e), onContextMenu: (e: Event) => !onPhoto(e) && long.onContextMenu(e) };
  return (
    <div class={`slip${open ? " open" : ""}${ringing ? " urgent" : ""}${earred ? " dog-ear" : ""}${photoId ? " photo-slip" : ""}`} style={style}>
      {earred && <DogEarSheet />}
      <PixelIcon name="pinned" className="slip-pin" />
      {open && photoId && (
        // The window takes the photo's own shape once it loads (NOTE_PHOTO openMaxHeight), so the
        // inset's edges (styles.css .slip-photo-open.fit) are the photo's, never a letterbox's.
        <span class={photoRatio ? "slip-photo-open fit" : "slip-photo-open"} style={photoRatio ? { width: `min(${Math.round(100 - 200 * NOTE_PHOTO.border)}%, ${Math.floor(NOTE_PHOTO.openMaxHeight * photoRatio)}px)` } : undefined} onClick={onToggle}>
          <img
            src={api.photos.url(photoId)}
            alt=""
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) setPhotoRatio(img.naturalWidth / img.naturalHeight);
            }}
          />
        </span>
      )}
      {ringing && (
        <span class="bang" aria-hidden="true">
          <PixelIcon name="bang" />
        </span>
      )}
      <button type="button" class={open ? "slip-paper" : "slip-paper closed"} aria-describedby={open ? undefined : SLIP_HINT_ID} aria-expanded={open} aria-label={open ? undefined : `${noteSlipLabel(note)}${hearts.length > 0 ? `, ${heartsCountLabel(hearts.length)}` : ""}`} onClick={onToggle} data-hold={holdable ? "" : undefined} {...hold}>
        {open ? (
          <>
            <span class="slip-head">
              <span>
                <strong>{note.created_by}</strong> · {formatAgo(note.created_at, now)}
              </span>
              <span class="slip-meta">{noteMetaLine(note, me, now)}</span>
            </span>
            {note.body && (
              <span class="slip-body">
                <MentionText text={note.body} />
              </span>
            )}
          </>
        ) : photoId ? (
          // The Polaroid (shared/notes.ts NOTE_PHOTO): the square photo in the paper's thin border, the
          // deep strip under it carrying the author's stamp and the caption, clamped to two lines. The
          // thumb behind the full photo while it loads.
          <>
            <span class="slip-photo" style={{ backgroundImage: `url("${api.photos.thumbUrl(photoId)}")` }}>
              <img src={api.photos.url(photoId)} alt="" loading="lazy" decoding="async" />
            </span>
            <span class="slip-photo-margin">
              <PeonStamp name={note.created_by} label={note.created_by} />
              {note.body && (
                <span class="slip-text slip-caption">
                  <MentionText text={noteSlipText(note.body)} />
                </span>
              )}
            </span>
          </>
        ) : (
          <>
            <PeonStamp name={note.created_by} label={note.created_by} />
            <span class="slip-text">
              <MentionText text={noteSlipText(note.body)} />
            </span>
          </>
        )}
      </button>
      {/* The closed slip's hearts: a small pixel heart and the count, out of the flow in the paper's
          bottom-right corner, so the slip is no taller and nothing on it moves. For anyone who may
          heart the note it is a button of its own beside the paper's (never inside it), a 44 square
          over the corner that toggles your heart and never opens the slip; at zero hearts only the
          outline. On your own note, display only, and only once it has hearts. */}
      {!open && mayHeart && (
        <button type="button" class="slip-heart-tag" aria-label={mine ? "Remove your heart" : "Heart this note"} aria-pressed={mine} onClick={tapCorner}>
          <span class={hearts.length > 0 ? "slip-hearts" : "slip-hearts empty"} aria-hidden="true" onAnimationEnd={() => setPopping(false)}>
            <NoteHeart key={cornerPops} filled={mine} className={popping && mine ? "note-heart pop" : "note-heart"} />
            {hearts.length > 0 && <span class="slip-hearts-count">{hearts.length}</span>}
          </span>
        </button>
      )}
      {!open && !mayHeart && hearts.length > 0 && (
        <span class="slip-hearts" aria-hidden="true">
          <NoteHeart filled />
          <span class="slip-hearts-count">{hearts.length}</span>
        </span>
      )}
      {open && (ringing || mayRemove || showHearts) && (
        // The paper around the buttons folds the note too; only the buttons themselves do their own thing.
        <div class="slip-actions" onClick={(e) => e.target === e.currentTarget && onToggle()}>
          {showHearts && (
            <span class="slip-heart-row">
              {mayHeart ? (
                <button type="button" class="slip-heart" aria-label={mine ? "Remove your heart" : "Heart this note"} aria-pressed={mine} onClick={heart}>
                  <NoteHeart key={pops} filled={mine} scale={3} className={pops > 0 && mine ? "note-heart pop" : "note-heart"} />
                </button>
              ) : (
                <span class="slip-heart-still">
                  <NoteHeart filled scale={3} />
                </span>
              )}
              {hearts.length > 0 && (
                <span class="slip-hearts-who">
                  <span class="sr-only">Hearted by </span>
                  {noteHeartsLabel(hearts, me)}
                </span>
              )}
            </span>
          )}
          {ringing && (
            <button type="button" class="btn btn-primary note-stop" onClick={stop}>
              Stop
            </button>
          )}
          {/* Only who may remove it sees Remove (shared/permissions.ts). A note still on its way to the
              server cannot be removed yet: the button waits, quietly, since removing is not what is busy. */}
          {mayRemove && (
            <button type="button" class="btn-quiet" onClick={remove} disabled={sending}>
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * `notes` comes from the board's one request (api.board.summary, T5) instead
 * of this component fetching /api/notes on its own; the board is the only
 * screen that mounts PinnedNotes (grepped), so there is no other caller to
 * keep an own-fetch fallback for. Local state still exists so pinning and
 * removing a note feel instant; it resyncs from `notes` on the board's next
 * live refresh, which by then carries the server's own list.
 */
export function PinnedNotes({ peonName, role, now, notes: notesProp, lead, openNote = null, speedrun: speedrunProp = null, slop: slopProp = null, week = null, showTheme = false, showGoal = false }: { peonName: string; role: Role; now: number; notes: Note[] | null; lead?: ComponentChildren; /** A name tag's link: the note to open (docs/mentions.md). */ openNote?: string | null; /** The speedrun note (docs/notes.md "The speedrun note"), only while speedrunUI is on; null draws nothing. */ speedrun?: SpeedrunNote | null; /** The slop tally (docs/notes.md "The slop tally"), only while slopTallyUI is on; null draws nothing. */ slop?: SlopTally | null; /** The house's week (docs/house-week.md): the theme's banner (showTheme) and the goal's sign (showGoal); null draws nothing. */ week?: HouseWeekSummary | null; showTheme?: boolean; showGoal?: boolean }) {
  const [notes, setNotes] = useState<Note[] | null>(notesProp);
  useEffect(() => setNotes(notesProp), [notesProp]);
  // A write from its sheet shows at once; the board's next read brings the server's own.
  const [speedrun, setSpeedrun] = useState<SpeedrunNote | null>(speedrunProp);
  useEffect(() => setSpeedrun(speedrunProp), [speedrunProp]);
  // A reset shows at once; the board's next read (the reset bumps the version) brings the server's own.
  const [slop, setSlop] = useState<SlopTally | null>(slopProp);
  useEffect(() => setSlop(slopProp), [slopProp]);
  const timersUI = useTimersUI();
  const heartsUI = useHeartsUI();
  const photoNotesUI = usePhotoNotesUI();
  // The board's minute tick (and every notes refresh) re-renders this; the clock is read here too,
  // because a refresh that brings a note's ring can land up to a minute after the last tick.
  const clock = Math.max(now, Date.now());
  // A ring starting or stopping (here, from a push, or by itself) redraws the cards.
  const [, setRingTick] = useState(0);
  useEffect(() => {
    const redraw = () => setRingTick((n) => n + 1);
    window.addEventListener(RING_EVENT, redraw);
    return () => window.removeEventListener(RING_EVENT, redraw);
  }, []);
  // The ring check: on every notes refresh and every tick, a note fired for this person and not
  // stopped on this device rings (once per page load; src/ring.ts keeps it to one ring at a time).
  // A ring already sounding stops the moment its note comes back acknowledged (someone else got it).
  useEffect(() => {
    const list = (notes ?? []).filter((n) => !n.id.startsWith(LOCAL_PREFIX));
    stopIfAcknowledged(list);
    const due = noteToRing(list, peonName, Date.now());
    if (due) startRing(due.id);
  }, [notes, now, peonName]);
  // The composer's timer: the wheels at 0:0:0 by default (an ordinary note) and folded behind the
  // alarm bell; above zero, who it is for.
  const [ringParts, setRingParts] = useState<TimerParts>(NO_TIMER);
  const [ringOpen, setRingOpen] = useState(false);
  const timed = timerMs(ringParts) > 0;
  const [picked, setPicked] = useState<Picked>("everyone");
  const [roster, setRoster] = useState<string[] | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  /** A fresh sheet each time it opens (and when a refused pin opens it again with the words kept). */
  const [composerKey, setComposerKey] = useState(0);
  const [draft, setDraft] = useState("");
  /** A photo note's photo (docs/notes.md "Photo notes"), once the "Looks good?" check has it; only while photoNotesUI is on. */
  const [photoId, setPhotoId] = useState<string | null>(null);
  const withPhoto = photoNotesUI && photoId !== null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The composer is a sheet over the board (owner, 2026-10-05: "The location where the form to enter
  // stuff for the note page is confusing, should be a modal that opens over it"; ComposerSheet): its
  // field takes the focus as it opens, Escape and the dim are its Cancel, and once it has closed the
  // focus goes back to the board's add slip ("Pin a note"), the board not scrolling for it.
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (composerOpen) {
      opened.current = true;
    } else if (opened.current) {
      opened.current = false;
      addRef.current?.focus({ preventScroll: true });
    }
  }, [composerOpen]);
  function openComposer() {
    if (composerOpen) return;
    holdKeyboard();
    setComposerKey((k) => k + 1);
    setComposerOpen(true);
  }
  useEffect(() => {
    if (!composerOpen || !timersUI || roster) return;
    api.peons.list().then(
      ({ names }) => setRoster(rosterWithMe(names, peonName)),
      () => setRoster([peonName]),
    );
  }, [composerOpen, timersUI, roster, peonName]);
  /** The sheet has gone: after Cancel (the dim, Escape) everything typed goes too; after a pin it is already on its way. */
  function closed(done: boolean) {
    setComposerOpen(false);
    if (done) return;
    setDraft("");
    setPhotoId(null);
    setRingParts(NO_TIMER);
    setRingOpen(false);
    setPicked("everyone");
    setError(null);
    setMissing(null);
  }

  const active = (notes ?? []).filter((n) => isNoteActive(n, new Date(clock)));
  /** The one note open on the board; opening another folds it. */
  const [openId, setOpenId] = useState<string | null>(openNote);
  useEffect(() => {
    if (openNote) setOpenId(openNote);
  }, [openNote]);
  const chars = Array.from(draft).length;
  // Pin it stays live while the note is empty or too long (docs/ui-audit.md triage item 1): a press
  // says so and puts the focus in the field. The line goes once the note will do.
  const unready = noteMissing({ draft, photo: withPhoto });
  const [missing, setMissing] = useState<Missing | null>(null);
  useEffect(() => {
    if (missing && !unready) setMissing(null);
  }, [missing, unready]);

  // Pinning and removing answer at once, on the press: the note
  // appears or goes on the press, and comes back (or goes again) with the server's words if the
  // request fails. A removal's error shows above the notes, since its card has gone; a pin's under
  // the composer, whose sheet opens again with the words still in it.
  const [removeError, setRemoveError] = useState<string | null>(null);

  async function removeNote(note: Note) {
    setRemoveError(null);
    setNotes((cur) => (cur ? cur.filter((n) => n.id !== note.id) : cur));
    try {
      await api.notes.remove(note.id);
    } catch (e) {
      setNotes((cur) => (cur && !cur.some((n) => n.id === note.id) ? [...cur, note].sort(newestFirst) : cur));
      setRemoveError(e instanceof Error ? e.message : "Could not remove the note");
    }
  }

  // A heart answers at once, on the tap (docs/notes.md "Hearts"): the slip shows it, the request runs
  // behind it, the server's hearts replace it, and a refusal puts it back with the server's words.
  // `on` is what the press asked for: the open slip's heart toggles, a long press only adds.
  async function heartNote(note: Note, on: boolean) {
    if (hasHearted(note.hearts, peonName) === on) return;
    setRemoveError(null);
    setNotes((cur) => (cur ? cur.map((n) => (n.id === note.id && hasHearted(n.hearts, peonName) !== on ? { ...n, hearts: toggleHeart(n.hearts, peonName) } : n)) : cur));
    try {
      const saved = await api.notes.heart(note.id, on);
      setNotes((cur) => (cur ? cur.map((n) => (n.id === note.id ? { ...n, hearts: saved.hearts ?? [] } : n)) : cur));
    } catch (e) {
      setNotes((cur) => (cur ? cur.map((n) => (n.id === note.id ? { ...n, hearts: note.hearts } : n)) : cur));
      setRemoveError(e instanceof Error ? e.message : "Could not heart the note");
    }
  }

  async function pin(close: (done?: boolean) => void) {
    const body = draft.trim();
    if (busy) return;
    if (unready) {
      setError(null);
      setMissing(unready);
      fieldRef.current?.focus();
      return;
    }
    void playAcknowledgement();
    const createdAt = new Date().toISOString();
    // No time picked (or the flag off): an ordinary note, posted exactly as before.
    const timer = timersUI ? timerRequest(ringParts, picked) : undefined;
    const photo = withPhoto ? photoId : null;
    const local: Note = {
      id: `${LOCAL_PREFIX}${createdAt}`,
      body,
      created_by: peonName,
      created_at: createdAt,
      expires_at: noteExpiresAt(createdAt),
      fire_at: timer ? noteFireAt(createdAt, timer.ring_in_ms) : null,
      recipients: timer ? timer.recipients : null,
      fired_at: null,
      acknowledged_at: null,
      acknowledged_by: null,
      rings: 0,
      hearts: [],
      photo_id: photo,
    };
    setBusy(true);
    setError(null);
    setNotes((cur) => [local, ...(cur ?? [])]);
    close(true);
    try {
      const note = await api.notes.create(body, peonName, timer, photo);
      setNotes((cur) => (cur ?? []).map((n) => (n.id === local.id ? note : n)));
      setDraft("");
      setPhotoId(null);
      setRingParts(NO_TIMER);
      setRingOpen(false);
      setPicked("everyone");
      void playPeonLine("note");
    } catch (e) {
      setNotes((cur) => (cur ? cur.filter((n) => n.id !== local.id) : cur));
      setComposerKey((k) => k + 1);
      setComposerOpen(true);
      setError(e instanceof Error ? e.message : "Could not pin note");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="pinned-notes">
      {removeError && <ErrorBox message={removeError} />}
      {/* The notice board: the bronze frame round a cork panel, always there to pin to. The lead (the
          week's scroll) first, then the notes' slips, then the add slip in the next open space (owner,
          2026-10-05: "There should be a pin/note with a plus on it on the board in the next open space
          rather than the existing add note button"). */}
      <div class="notice-board">
        {/* What a closed slip's description says (shared/notes.ts NOTE_SLIP_HINT): a tap opens it. Hidden, read only through aria-describedby. */}
        <span id={SLIP_HINT_ID} hidden>
          {NOTE_SLIP_HINT}
        </span>
        {/* The lead, given by the board: full width at the top, pinned at both top corners like a
            sheet. It brings its own paper; nothing here wraps it. */}
        {lead && (
          <div class="notice-lead">
            {lead}
          </div>
        )}
        {/* The slop tally (docs/notes.md "The slop tally"): a sign pinned straight across the cork under
            the lead and above the slips, never one of the columns, so it never moves a note about. */}
        {slop && <SlopSign tally={slop} now={clock} onReset={setSlop} />}
        {/* The house's week (docs/house-week.md): the theme's banner and the goal's sign, under the slop
            tally and above the slips, never one of the columns. */}
        {week && <WeekSign week={week} theme={showTheme} goal={showGoal} />}
        {/* The slips in columns, each under the shorter one in the board's order (src/components/NoticeSlips.tsx;
            owner, 2026-10-20: "Layout here looks kinda bad"), the add slip last, so it fills the hole. */}
        <NoticeSlips
          cells={[
            // The speedrun note, first: it never comes down, so it never moves the notes about.
            ...(speedrun ? [{ key: SPEEDRUN_SLIP_ID, open: openId === SPEEDRUN_SLIP_ID, node: <SpeedrunSlip note={speedrun} open={openId === SPEEDRUN_SLIP_ID} onToggle={() => setOpenId((cur) => (cur === SPEEDRUN_SLIP_ID ? null : SPEEDRUN_SLIP_ID))} me={peonName} role={role} onSaved={setSpeedrun} /> }] : []),
            ...active.map((n) => ({
              key: n.id,
              open: openId === n.id,
              node: <NoteSlip note={n} now={clock} me={peonName} open={openId === n.id} onToggle={() => setOpenId((cur) => (cur === n.id ? null : n.id))} onRemove={(note) => void removeNote(note)} onAck={(acked) => setNotes((cur) => (cur ? cur.map((x) => (x.id === acked.id ? acked : x)) : cur))} onHeart={(note, on) => void heartNote(note, on)} mayRemove={canDelete({ name: peonName, role }, n.created_by)} heartsUI={heartsUI} />,
            })),
            {
              key: ADD_SLIP_ID,
              open: false,
              // The add slip: a blank slip with an ink plus, last on the board. It opens the composer in a
              // sheet over the board, and stays put.
              node: (
                <div class="slip slip-add" style={{ transform: `rotate(${noteTilt(ADD_SLIP_ID)}deg)` }}>
                  <PixelIcon name="pinned" className="slip-pin" />
                  <button type="button" class="slip-paper note-add" ref={addRef} aria-label="Pin a note" onClick={openComposer}>
                    <span class="slip-plus" aria-hidden="true" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </div>
      {composerOpen && (
        <ComposerSheet key={composerKey} label="Pin a note" onClosed={closed}>
          {(close) => (
            <div class="note-composer">
              {/* The placeholder goes when typing starts; the field keeps its name for assistive tech. */}
              <label class="sr-only" for="note-composer-text">
                Note
              </label>
              <div class={`note-text${timersUI ? " has-bell" : ""}`}>
                <MentionField
                  as="textarea"
                  fieldRef={fieldRef}
                  pickerId="note-composer"
                  id="note-composer-text"
                  class="input"
                  rows={3}
                  maxLength={NOTE_MAX_CHARS}
                  placeholder={withPhoto ? NOTE_CAPTION_PLACEHOLDER : "What should everyone know?"}
                  value={draft}
                  onValue={setDraft}
                  autoFocus
                  data-autofocus
                />
                {/* The alarm bell (owner, 2026-10-05: "Rather than saying 'alarm.' There should just be an
                    alarm bell button in the bottom right of the note text box entry, when it is on it will
                    look ringing (static) and not otherwise"): in the box's bottom-right corner, after the
                    text in the tab order, plain at 0:0:0 and ringing once a time is set. A tap opens the
                    phone timer's three wheels under the box (each a select.input, a native wheel on an
                    iPhone); a tap again folds them and the value stays. 0:0:0 is no ring. */}
                {timersUI && (
                  <button type="button" class="note-bell" aria-label={`Alarm, ${timerPartsLabel(ringParts)}`} aria-expanded={ringOpen} aria-controls="note-ring-wheels" onClick={() => setRingOpen((v) => !v)}>
                    <PixelIcon name={timed ? "bell-ringing" : "bell"} />
                  </button>
                )}
              </div>
              {timersUI && (
                <>
                  {ringOpen && (
                    <div class="note-ring-wheels" id="note-ring-wheels">
                      {TIMER_WHEELS.map(({ unit, word }) => (
                        <label key={unit} class="note-ring-wheel">
                          <select class="input" value={String(ringParts[unit])} onChange={(e) => setRingParts((cur) => withTimerPart(cur, unit, e.currentTarget.value))}>
                            {Array.from({ length: TIMER_PART_MAX[unit] + 1 }, (_, v) => (
                              <option key={v} value={String(v)}>
                                {v}
                              </option>
                            ))}
                          </select>
                          <span class="note-ring-unit">{word}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {timed && (
                    <>
                      <span class="field-label" id="note-who-label">Who</span>
                      <div class="segmented note-chips" role="group" aria-labelledby="note-who-label">
                        <button type="button" class={isPicked(picked, "everyone") ? "active" : ""} aria-pressed={isPicked(picked, "everyone")} onClick={() => setPicked("everyone")}>
                          Everyone
                        </button>
                        {(roster ?? [peonName]).map((name) => (
                          <button key={name} type="button" class={isPicked(picked, name) ? "active" : ""} aria-pressed={isPicked(picked, name)} onClick={() => setPicked((cur) => togglePicked(cur, name, peonName))}>
                            {name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              {/* Photo notes (docs/notes.md "Photo notes"; owner, 2026-10-20: "Allow adding photos to the
                  board."): "Add a photo" opens the phone's own camera-or-library choice and the same
                  "Looks good?" check a task's photo takes; once in, a small instant photo and
                  "Remove photo". The words above are then its caption, and may be left empty. */}
              {photoNotesUI && <PhotoPicker variant="quiet" label={NOTE_ADD_PHOTO} clearLabel={NOTE_REMOVE_PHOTO} photoId={photoId} onChange={setPhotoId} />}
              <div class="note-composer-foot">
                <span class="muted small">{chars} / {NOTE_MAX_CHARS}</span>
                <div class="note-composer-actions">
                  <button type="button" class="btn-quiet" onClick={() => close()}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    class={`btn btn-primary${unready ? " btn-unready" : ""}`}
                    onClick={() => void pin(close)}
                    disabled={busy}
                    aria-describedby={missing ? "note-missing" : undefined}
                  >
                    <BusyLabel busy={busy} label="Pinning…">Pin it</BusyLabel>
                  </button>
                </div>
              </div>
              {missing && unready ? (
                <div class="form-error" role="alert" id="note-missing">{unready.line}</div>
              ) : (
                error && <ErrorBox message={error} />
              )}
            </div>
          )}
        </ComposerSheet>
      )}
    </div>
  );
}
